/* eslint-disable stylistic/comma-dangle */
/* eslint-disable sort-keys */
/**
 * King Living MegaMenu Theme
 *
 * Global mega menu styling system:
 * - Desktop: Trigger button styles, link text, popover content positioning
 * - Mobile: Panel styles, drawer footer styles
 *
 * @override Extends Chakra UI theme
 */

const MegaMenu = {
  parts: [
    // Desktop styles
    'megaMenuDesktopWrapper',
    'megaMenuDesktopNav',
    'menuItemDesktopContainer',
    'desktopTriggerButton',
    'desktopPopoverContent',
    'megaMenuLoadingSpinner',
    'saleBadge',
    // Mobile styles
    'mobileDrawerHeader',
    'mobileDrawerContent',
    'closeButton',
    'accountNameBadge',
    'logoMobileWrapper',
    'logoMobile',
    'mobileDrawerBody',
    'mobileDrawerBodyInner',
    'mobileSubcategoryPanel',
    'mobileSubcategoryPanelContent',
    'mobilePanelItemsList',
    'mobileDrawerFooter',
    'mobileDrawerFooterStack',
    'mobileDrawerFooterRow',
    'mobileMenuItemParentWrapper',
    'mobileDrawerFooterIconButton',
    'mobileDrawerLocaleSelector',
    'backButton',
    'backButtonWrapper',
    'menuItemIconButton',
    // Shared styles
    'menuItemLink',
    'menuItemButton',
    'menuItemText',
    'menuItemContainer',
    'menuItemIcon',
    'menuItemImages',
    'navigationColumnWrapper',
    'navigationColumnLinksWrapper',
    'navigationColumnTitle',
    'navigationColumnTitleSkeleton',
    'navigationColumnLinkSkeleton',
    'shopAllLinkWrapper',
  ],

  baseStyle: (props = {}) => {
    const {level = 0, isDesktop, isMenuOpen, isOpen, popoverTop} = props;

    // Level-based text styles
    const getTextStyles = () => {
      switch (level) {
        case 0:
          return {
            fontSize: '20px',
            fontWeight: '500',
          };
        case 1:
          return {
            fontSize: '18px',
            fontWeight: '500',
          };
        default:
          return {
            fontSize: '20px',
            fontWeight: '500',
          };
      }
    };

    return {
      // Desktop styles
      megaMenuDesktopWrapper: {
        width: '100%',
      },
      megaMenuDesktopNav: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: '56px', // Reserve space to prevent shift when loading
        paddingBottom: '0',
        borderBottom: '1px solid',
        borderColor: isMenuOpen ? 'brandColors.greyscale20' : 'transparent',
        transition: 'border-color 0.2s ease',
      },
      menuItemDesktopContainer: {
        maxWidth: '1440px',
        mx: 'auto',
        paddingX: {base: '20px', lg: '80px'},
        paddingTop: {base: '0', lg: '45px'},
        width: '100%',
      },
      desktopTriggerButton: {
        _hover: {
          opacity: 0.7,
          _after: {
            backgroundColor: 'brandColors.primaryDarkLinen', // Show dark underline on hover for all items
          },
        },
        alignItems: 'center',
        display: 'flex',
        fontSize: '16px',
        fontWeight: '500',
        position: 'relative',
        textDecoration: 'none', // Prevent link underline
        // Active indicator pseudo-element
        _after: {
          content: '""',
          position: 'absolute',
          bottom: '-17px',
          left: 0,
          right: 0,
          height: '2px',
          width: 'calc(100% + 10px)',
          backgroundColor: isOpen ? 'brandColors.primaryDarkLinen' : 'transparent',
          transition: 'background-color 0.2s ease',
        },
        sx: {
          '.chakra-link': {
            _hover: {
              textDecoration: 'none',
            },
          }
        },
      },
      desktopPopoverContent: {
        width: '100vw',
        maxWidth: '100vw',
        minWidth: '100vw',
        left: '0 !important',
        right: '0 !important',
        marginLeft: '0 !important',
        marginRight: '0 !important',
        marginTop: '0 !important',
        paddingTop: '0 !important',
        padding: 0,
        transform: 'none !important',
        // z-index lower than header (sticky = 1100) so popover appears from behind header
        zIndex: 1099,
        position: 'fixed',
        // Dynamic top positioning based on trigger element's bottom
        top: isDesktop && popoverTop > 0 ? `${popoverTop}px !important` : 'auto',
        // Remove default Chakra popover styles that cause flash
        backgroundColor: 'background.primary',
        boxShadow: 'none',
        border: 'none',
        borderRadius: 0,
      },

      megaMenuLoadingSpinner: {
        color: 'brandColors.primaryDarkLinen',
        size: 'md',
      },

      saleBadge: {
        display: 'inline-block',
        backgroundColor: '#BF1619',
        borderRadius: 'md',
        height: '10px',
        width: '10px',
        marginRight: '4px',
      },

      // Mobile styles
      mobileDrawerHeader: {
        borderBottom: '1px solid',
        borderColor: 'brandColors.greyscale10',
        boxShadow: 'none',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      mobileDrawerContent: {
        backgroundColor: 'background.primary',
        height: '100dvh',
        maxHeight: '100dvh',
      },
      closeButton: {
        height: '20px',
        width: '20px',
        minWidth: 'auto',
      },
      accountNameBadge: {
        backgroundColor: 'white',
        borderRadius: '20px',
        color: 'brandColors.greyscale80',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        fontSize: '11px',
        minWidth: '20px',
      },
      accountNameBadgeNotLoggedIn: {
        backgroundColor: 'transparent',
        sx: {
          '.chakra-icon': {
            color: 'brandColors.primaryDarkLinen',
          }
        }
      },
      logoMobile: {
        width: '100%',
        height: 'auto',
      },
      logoMobileWrapper: {
        display: 'flex',
        maxWidth: {base: '76px', lg: '112px'},
        maxHeight: {base: '17px', lg: '24px'},
      },
      mobileDrawerBody: {
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0, // Allow flex children to shrink below content size
        height: '100%',
      },
      mobileDrawerBodyInner: {
        height: '100%',
        position: 'relative',
        width: '100%',
        willChange: 'transform', // Optimize transform animations
      },
      mobileSubcategoryPanel: {
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 20px',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom))', // Account for safe area
        height: '100%',
        minHeight: 0, // Allow flex children to shrink
        left: 0,
        position: 'absolute',
        top: 0,
        transition: 'transform 0.3s ease-in-out',
        width: '100%',
        willChange: 'transform',
        boxSizing: 'border-box',
      },
      mobileDrawerLocaleSelector: {
        sx: {
          '.chakra-button': {
            width: 'auto',
            display: 'flex',
            gap: '16px',
          }
        }
      },
      mobileSubcategoryPanelContent: {
        flex: 1,
        minHeight: 0, // Critical for flex scrolling - allows content to shrink and enable scroll
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
      },

      mobilePanelItemsList: {
        padding: '0',
      },

      mobileDrawerFooter: {
        padding: '20px 0 calc(4px + env(safe-area-inset-bottom)) 0', // Add safe area padding
        flexShrink: 0, // Prevent footer from shrinking
        marginTop: 'auto', // Push footer to bottom
      },

      mobileDrawerFooterStack: {
        alignItems: 'flex-start',
        spacing: 0,
      },
      mobileDrawerFooterRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        columnGap: '16px',
        sx: {
          '.chakra-button': {
            height: '20px',
            width: '20px',
            minWidth: '20px',
          },
          '.mobile-drawer-footer-text': {
            color: 'brandColors.itemMenu',
          },
        }
      },
      mobileDrawerFooterIconButton: {
        color: 'brandColors.primaryDarkLinen',
        height: '20px',
        width: '20px',
        minWidth: '20px',
        sx: {
          svg: {
            width: '100%',
            height: '100%',
          }
        }
      },
      mobileMenuItemParentWrapper: {
        color: 'brandColors.itemMenu',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '20px',
        lineHeight: 1.2,
        padding: '12px',
        width: '100%',
      },

      menuItemIconButton: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        display: 'flex',
        justifyContent: 'center',
        ml: 2,
        p: 1,
      },
      // Shared styles
      menuItemLink: {
        alignItems: 'center',
        color: 'brandColors.greyscale90',
        justifyContent: 'space-between',
        textDecoration: 'none',
        width: '100%',
        _hover: {
          color: 'brandColors.primaryDarkLinen',
          textDecoration: 'underline',
        },
      },

      menuItemButton: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        cursor: 'pointer',
        justifyContent: 'space-between',
        p: 0,
        textAlign: 'left',
        width: '100%',
      },

      menuItemText: {
        ...getTextStyles(),
      },

      menuItemContainer: {
        marginBottom: '32px',
      },

      menuItemIcon: {
        height: '13px',
        width: '13px',
      },

      menuItemImages: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: {base: '4px', lg: '16px'},
        mt: {base: '16px', lg: '0'},
        overflowX: {base: 'auto', lg: 'visible'},
        sx: {
          '.menu-item-image': {
            minWidth: {base: '258px', lg: 'auto'},
          },
        },
      },
      // Navigation column styles
      navigationColumnWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '16px',
        minWidth: {base: 'auto', lg: '160px'},
      },
      navigationColumnLinksWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        sx: {
          '.chakra-link': {
            color: 'brandColors.itemMenu',
            fontSize: {base: '18px', lg: '14px'},
            fontWeight: 500,
          },
        },
      },
      navigationColumnTitle: {
        fontSize: '10px',
        fontWeight: 600,
        color: 'brandColors.primaryDarkLinen',
        letterSpacing: '2px',
        textTransform: 'uppercase',
      },

      navigationColumnTitleSkeleton: {
        borderRadius: '2px',
      },

      navigationColumnLinkSkeleton: {
        borderRadius: '2px',
      },
      backButton: {
        color: 'brandColors.greyscale90',
        fontSize: '14px',
        fontWeight: 500,
        height: 'auto',
        padding: 0,
        minHeight: 'auto',
      },
      backButtonWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginBottom: '28px',
        sx: {
          '.chakra-button__icon': {
            height: '20px',
            width: '20px',
            marginRight: '0',
          },
          'svg': {
            width: '100%',
            height: '100%',
          }
        }
      },
      shopAllLinkWrapper: {
        marginBottom: '28px',
        marginTop: {base: '0', lg: '40px'},
        sx: {
          'a': {
            alignItems: 'center',
            color: {base: 'brandColors.itemMenu', lg: 'black'},
            display: 'flex',
            gap: '12px',
            fontSize: {base: '20px', lg: '14px'},
            fontWeight: {base: 400, lg: 600},
          },
          '.chakra-icon': {
            color: 'brandColors.primaryDarkLinen',
            height: 'auto',
            width: {base: '13px', lg: '16px'},
          },
        },
      },
    };
  },
};

export default MegaMenu;
